import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { formatUserRoleType } from "@utils/data-display";

/**
 * The workspace roles that only a member holding a Sphere XG seat may take on, since they let the
 * member make changes: admins, group managers and project managers.
 */
const XG_SEAT_WORKSPACE_ROLES = new Set(["COMPANY_EXECUTIVE", "COMPANY_MANAGER", "PROJECT_MANAGER"]);

/** Why a team cannot be given a Sphere XG seat, nor any role that needs one */
export const TEAM_XG_SEAT_REASON = "Teams cannot hold a Sphere XG seat";

/**
 * Returns why a team cannot take on a role that needs a Sphere XG seat, and which role it can take
 * on instead, e.g. "Teams cannot hold a Sphere XG seat, so they can only be Group Viewer." It is
 * the same for every subject a role is changed in: the workspace, a group or a project.
 *
 * @param viewerRole The role of the subject that a team can take on, as it needs no seat
 */
export function getTeamXgSeatRoleMessage(viewerRole) {
  return `${TEAM_XG_SEAT_REASON}, so they can only be ${formatUserRoleType(viewerRole)}.`;
}

/**
 * Returns why a team cannot take on a role that needs a Sphere XG seat, for a subject that offers
 * teams more than one role without a seat, e.g. "Teams cannot hold a Sphere XG seat, so they cannot
 * be Admin." See getTeamXgSeatRoleMessage for a subject with a single viewer role.
 *
 * @param role The role that needs a seat
 */
export function getTeamXgSeatBlockedRoleMessage(role) {
  return `${TEAM_XG_SEAT_REASON}, so they cannot be ${formatUserRoleType(role)}.`;
}

/** Whether a member needs a Sphere XG seat to take on a workspace role */
export function doesWorkspaceRoleRequireXgSeat(role) {
  return XG_SEAT_WORKSPACE_ROLES.has(role);
}

/**
 * Returns why a member cannot be given a Sphere XG seat, or undefined when they can be given one.
 *
 * Any member can hold a seat, including one that did not accept their workspace invite yet. Teams
 * are not users at all and can therefore never hold one.
 */
export function getXgSeatBlockReason(member) {
  if (FaroCoreApiUtils.Member.isTeam(member)) {
    return TEAM_XG_SEAT_REASON;
  }
  return undefined;
}

/**
 * Returns the keys to look members up in the active members, which are their member ID and email.
 *
 * The email is included because a pending member can be listed with an ID in one response and
 * without it in the other, while the email is the same in both.
 */
export function getActiveMemberKeys(members) {
  return new Set(members.flatMap(member => getMemberKeys(member)));
}

/**
 * Whether a member is one of the active members of the workspace, who hold a Sphere XG seat when
 * the plan limits the active users.
 *
 * @param member The member to check
 * @param activeMemberKeys The keys of the active members of the workspace, see getActiveMemberKeys
 */
export function isActiveMember(member, activeMemberKeys) {
  return getMemberKeys(member).some(key => activeMemberKeys.has(key));
}

/** Returns the keys that identify a member in the active members */
function getMemberKeys(member) {
  if (FaroCoreApiUtils.Member.isTeam(member)) {
    return [];
  }
  const keys = [FaroCoreApiUtils.Member.getMemberId(member)];
  if ("email" in member && member.email) {
    keys.push(member.email.toLowerCase());
  }
  return keys;
}